import { useAsync } from 'react-use';

import * as Sentry from 'lib/client/sentry/sentry';
import { CONTENTREPORTINGAPI_URL } from 'containers/ContentReporting/ContentPolicyReporting/utils/constants';
import { ErrorMessage } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { GetUserDataTypeResponse, ReportOriginType } from 'proto/generated/tintin_content_policy';

export const useReporterInfo = (
  username: string | undefined = undefined,
  email: string | undefined = undefined,
  reportOriginType: ReportOriginType = ReportOriginType.REPORT_ORIGIN_INVALID,
  refresh: boolean,
) => {
  const { value, loading, error } = useAsync(async (): Promise<GetUserDataTypeResponse | undefined> => {
    // Skip API call on non-reporting pages (maintenance, thank-you, 404, etc.)
    if (reportOriginType === ReportOriginType.REPORT_ORIGIN_INVALID) {
      return undefined;
    }

    if (!username && !email) {
      return undefined;
    }

    const userData = username ? { username } : { email };
    const requestBody = { ...userData, reportOriginType: ReportOriginType[reportOriginType] };

    const options = {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };

    try {
      const response = await fetch(`${CONTENTREPORTINGAPI_URL}/v1/user-data`, options);

      if (response.ok) {
        return response.json();
      }

      if (!response.ok) {
        if (response.status === 429) {
          return Promise.reject(ErrorMessage.RATE_LIMITING_ERROR);
        }
        if (email) {
          Sentry.captureException(
            `Could not retrieve geo-headers for logged out User, when calling "/v1/user-data", Code: ${response.status}; Payload: ${response.body}`,
          );
        }
      }

      Sentry.captureException(`Called "/v1/user-data", Code: ${response.status}; Payload: ${response.body}`);
    } catch (e: unknown) {
      Sentry.captureException(`Error under useUserInfo: ${JSON.stringify(e)}`);
    }
    return Promise.reject(ErrorMessage.CONTENT_REQUEST_ERROR);
  }, [username, email, reportOriginType, refresh]);

  return {
    isLoading: loading,
    error,
    emailType: !!value ? value.emailType : undefined,
    country: !!value ? value.country : undefined,
  };
};

export default useReporterInfo;
